import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../../components/public/Navbar';
import Footer from '../../components/public/Footer';
import PageHeader from '../../components/public/PageHeader';
import CompanyOverview from '../../components/public/CompanyOverview';
import VisionMission from '../../components/public/VisionMission';
import Timeline from '../../components/public/Timeline';
import CoreValues from '../../components/public/CoreValues';
import TeamSection from '../../components/public/TeamSection';
import StatsSection from '../../components/public/StatsSection';
import CTASection from '../../components/public/CTASection';
import HeroSection from '../../components/public/HeroSection';
import FeaturesSection from '../../components/public/FeaturesSection';
import ProductsSection from '../../components/public/ProductsSection';
import ServicesSection from '../../components/public/ServicesSection';
import TestimonialsSection from '../../components/public/TestimonialsSection';
import { publicApi } from '../../services/api';
import { parseSectionContent } from '../../utils/sectionContent';

const SECTION_COMPONENTS = {
  hero: HeroSection,
  features: FeaturesSection,
  services: ServicesSection,
  products: ProductsSection,
  testimonials: TestimonialsSection,
  cta: CTASection,
  overview: CompanyOverview,
  vision_mission: VisionMission,
  timeline: Timeline,
  core_values: CoreValues,
  team: TeamSection,
  stats: StatsSection,
  content: ({ content }) => (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 prose max-w-none">
        <div dangerouslySetInnerHTML={{ __html: content?.body || '' }} />
      </div>
    </section>
  ),
  gallery: ({ content }) => (
    <section className="py-16 bg-bg-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {content?.title && <h2 className="text-3xl font-extrabold text-center mb-4" style={{ fontFamily: "'Cabin', sans-serif" }}>{content.title}</h2>}
        {content?.subtitle && <p className="text-center text-gray-500 mb-8">{content.subtitle}</p>}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {(content?.images || []).map((img, i) => (
            <div key={i} className="aspect-square bg-gray-200 rounded-xl overflow-hidden">
              {img.url ? (
                <img src={img.url} alt={img.alt || ''} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-4xl">🖼️</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  ),
};

export default function DynamicPage() {
  const { slug } = useParams();
  const [sections, setSections] = useState([]);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await publicApi.getPage(slug);
      const data = res.data?.data || res.data || null;
      setPage(data || null);
      setSections(data?.sections || []);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Halaman tidak ditemukan.');
      } else {
        setError('Gagal memuat data. Silakan coba lagi.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-light">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Memuat...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="min-h-[70vh] flex items-center justify-center bg-bg-light pt-20">
          <div className="text-center bg-white p-8 rounded-2xl shadow-lg max-w-md">
            <span className="text-4xl mb-4 block">⚠️</span>
            <p className="text-red-600 mb-4">{error}</p>
            <div className="flex gap-3 justify-center">
              <button onClick={fetchData} className="bg-brand text-navy px-6 py-2 rounded-full hover:bg-brand-dark transition-colors text-sm font-semibold">Coba Lagi</button>
              <Link to="/" className="bg-gray-100 text-gray-700 px-6 py-2 rounded-full hover:bg-gray-200 transition-colors text-sm">Kembali ke Beranda</Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <PageHeader title={page?.title || slug} subtitle={page?.content || ''} />

      {/* Dynamic Sections */}
      {sections.length > 0 ? (
        sections
          .filter(s => s.is_active)
          .sort((a, b) => a.order - b.order)
          .map((section) => {
            const Component = SECTION_COMPONENTS[section.type];
            if (!Component) {
              console.warn(`Unknown section type: ${section.type}`);
              return null;
            }
            const parsedContent = parseSectionContent(section.content);
            return <Component key={section.id} content={parsedContent} />;
          })
      ) : (
        /* No sections — show empty state */
        <section className="py-32 bg-bg-light text-center">
          <div className="max-w-2xl mx-auto px-4">
            <span className="text-6xl block mb-6">📝</span>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Konten Belum Tersedia</h2>
            <p className="text-gray-500 mb-8">
              Halaman "{page?.title || slug}" sudah dibuat tetapi belum memiliki konten.{' '}
              Tambahkan <strong>Seksi</strong> melalui CMS Admin untuk menampilkan konten di halaman ini.
            </p>
            <Link to="/admin/sections" className="inline-block bg-brand text-navy px-6 py-3 rounded-full font-semibold hover:bg-brand-dark transition-colors">
              Buka CMS Admin
            </Link>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}